/**
 * Hit Hero - cart drawer click handlers (free-shipping bar "Add 1 More" + Mystery upsell "Add").
 *
 * Loaded unconditionally from snippets/scripts.liquid on every page.
 *
 * These handlers CANNOT live inside hh-cart-shipbar.liquid / hh-cart-upsell.liquid, because
 * cart-drawer.liquid only renders those snippets when the cart is non-empty:
 *
 *   {%- if cart.empty? -%} ... {%- else -%} {% render 'hh-cart-shipbar' %} ... {%- endif -%}
 *
 * So a visitor arriving with an empty cart never receives the inline script. They then add to
 * cart, the drawer arrives via the Section Rendering API, and scripts inside that swapped-in
 * HTML never execute (section-renderer.js parses it with DOMParser, whose script elements are
 * inert on insertion). Net result before this file existed: both buttons rendered but were
 * completely dead for every new visitor.
 *
 * Both listeners are delegated on document, so they bind once here and keep working across
 * every subsequent drawer swap.
 */
(function () {
  // Locale-safe cart route. A hard-coded "/cart/add.js" bypasses the localized cart route in a
  // subfolder market (for example /en-ca/) and can return wrongly localized section HTML.
  function hhRoot() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r ? r : '/';
  }

  function hhFetchCart() {
    return fetch(hhRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      });
  }

  function hhStandardCart(cart) {
    var currency = cart && cart.currency ? String(cart.currency) : '';
    return {
      id: cart && cart.token ? String(cart.token) : '',
      totalQuantity: Number(cart && cart.item_count) || 0,
      cost: {
        totalAmount: {
          amount: ((Number(cart && cart.total_price) || 0) / 100).toFixed(2),
          currencyCode: currency,
        },
      },
      lines: cart && Array.isArray(cart.items) ? cart.items.map(function (item) {
        return {
          id: String(item.key || item.id || ''),
          quantity: Number(item.quantity) || 0,
          cost: {
            totalAmount: {
              amount: ((Number(item.final_line_price) || 0) / 100).toFixed(2),
              currencyCode: currency,
            },
          },
        };
      }) : [],
      discountCodes: cart && Array.isArray(cart.discount_codes) ? cart.discount_codes.map(function (discount) {
        return { applicable: discount.applicable !== false, code: String(discount.code || '') };
      }) : [],
    };
  }

  function hhDispatchCartLinesUpdate(cart, items, sections, source, sectionId) {
    var detail = {
      didError: false,
      itemCount: Number(cart.item_count) || 0,
      items: cart.items,
      sections: sections,
      source: source,
    };
    var event = new Event('shopify:cart:lines-update', { bubbles: true });
    event.action = 'add';
    event.context = 'cart';
    event.lines = items.map(function (item) {
      return { merchandiseId: String(item.id), quantity: item.quantity };
    });
    event.detail = { source: source };
    event.promise = Promise.resolve({ cart: hhStandardCart(cart), detail: detail });
    var target = document.querySelector('#shopify-section-' + sectionId + ' cart-items-component') ||
      document.querySelector('#shopify-section-cart-drawer-section cart-items-component') ||
      document;
    target.dispatchEvent(event);
  }

  function hhCartAfterAdd() {
    return hhFetchCart().catch(function (error) {
      error.hhCartTruthFailed = true;
      throw error;
    });
  }

  /* ---------------------------------------------------------------------
   * Free-shipping progress bar
   * ------------------------------------------------------------------ */
  if (!window.__hhShipbarBound) {
    window.__hhShipbarBound = true;

    // Optimistic paint. The bar's state is pure Liquid, so it is only authoritative after the
    // Section Rendering API swap completes (a network round trip, which reads as lag on a slow
    // in-app connection). This repaints the instant a quantity button is tapped; the SRA
    // re-render then confirms with the server value, so any drift self-corrects.
    var repaint = function (delta) {
      var bar = document.querySelector('[data-hh-shipbar]');
      if (!bar) return;
      var min = Number(bar.getAttribute('data-min'));
      if (!isFinite(min) || min < 1) min = 2;
      var qty = Number(bar.getAttribute('data-qty'));
      if (!isFinite(qty)) return;
      qty = qty + delta;
      if (qty < 0) qty = 0;
      bar.setAttribute('data-qty', String(qty));
      var shown = qty > min ? min : qty;
      var remaining = min - qty;
      if (remaining < 0) remaining = 0;
      var fill = bar.querySelector('.hh-shipbar__fill');
      var track = bar.querySelector('.hh-shipbar__track');
      var msg = bar.querySelector('.hh-shipbar__msg');
      var count = bar.querySelector('.hh-shipbar__count');
      if (fill) fill.style.width = Math.round((shown * 100) / min) + '%';
      if (track) track.setAttribute('aria-valuenow', String(shown));
      if (count) count.textContent = shown + ' of ' + min;
      // Copy comes from the bar's own data attributes (snippets/hh-cart-shipbar.liquid), so this
      // optimistic paint and the section re-render that follows it can never disagree on wording.
      if (qty >= min) {
        if (bar.className.indexOf('hh-shipbar--unlocked') === -1) {
          bar.className += ' hh-shipbar--unlocked';
        }
        if (msg) msg.textContent = bar.getAttribute('data-msg-unlocked') || msg.textContent;
      } else {
        bar.className = bar.className.replace(' hh-shipbar--unlocked', '');
        if (msg) {
          var template = qty === 0 ? bar.getAttribute('data-msg-empty') : bar.getAttribute('data-msg-locked');
          if (template) msg.textContent = template.replace('{n}', String(remaining));
        }
      }
    };

    // Native cart line steppers: repaint immediately when the changed line is a Hit Hero.
    // CAPTURE phase (third arg true) is required: the theme's own quantity component
    // disables the button synchronously while its request is in flight, so a bubble-phase
    // listener would see disabled=true and bail before repainting.
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target || !e.target.closest) return;
        var q = e.target.closest('button[name="plus"], button[name="minus"]');
        if (!q || q.disabled) return;
        var bar = document.querySelector('[data-hh-shipbar]');
        if (!bar) return;
        var host = q.closest('[data-variant-id]');
        if (!host) return;
        var list = ',' + (bar.getAttribute('data-guard-variants') || '');
        if (list.indexOf(',' + host.getAttribute('data-variant-id') + ',') === -1) return;
        repaint(q.getAttribute('name') === 'plus' ? 1 : -1);
      },
      true
    );

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[data-hh-shipbar-btn]');
      if (!btn || btn.disabled) return;
      var el = btn.closest('[data-hh-shipbar-add]');
      if (!el) return;
      var id = el.getAttribute('data-variant');
      if (!id) return;
      // Snapshot the pre-click quantity so a FAILED add (sold out, stock limit, market block)
      // can roll the optimistic paint back instead of leaving a false "free shipping" promise.
      var bar0 = document.querySelector('[data-hh-shipbar]');
      var prevQty = bar0 ? bar0.getAttribute('data-qty') : null;
      // Set the moment the POST is accepted. Everything after that point is display work, and
      // display work failing must never be reported as the add failing. Same rule, and the same
      // bug, as hh-buy-card.js.
      var posted = false;
      repaint(1);
      var lbl = el.querySelector('[data-hh-shipbar-label]');
      var spin = el.querySelector('[data-hh-shipbar-spin]');
      var section = btn.closest('.shopify-section');
      var sectionId = section ? section.id.replace('shopify-section-', '') : 'cart-drawer-section';
      btn.disabled = true;
      if (spin) spin.hidden = false;
      if (lbl) lbl.textContent = 'Adding';
      fetch(hhRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      })
        .then(function (r) {
          if (!r.ok) throw r;
          posted = true;
          return r.json();
        })
        .then(function (data) {
          return hhCartAfterAdd().then(function (cart) {
            return { data: data, cart: cart };
          });
        })
        .then(function (transaction) {
          var data = transaction.data;
          var cart = transaction.cart;
          var html = data.sections && data.sections[sectionId];
          if (html) {
            var wrap = document.getElementById('shopify-section-' + sectionId);
            var fresh = new DOMParser()
              .parseFromString(html, 'text/html')
              .getElementById('shopify-section-' + sectionId);
            if (wrap && fresh) {
              wrap.replaceWith(fresh);
            }
            if (sectionId === 'cart-drawer-section') {
              var d = document.getElementById('cart-drawer');
              if (d) {
                if (typeof d.open === 'function') {
                  d.open();
                } else {
                  d.setAttribute('open', '');
                }
              }
            }
          } else {
            // Cart WAS mutated but no section came back. Never leave stale UI on screen.
            window.location.reload();
            return;
          }
          var sections = {};
          sections[sectionId] = html;
          hhDispatchCartLinesUpdate(
            cart,
            [{ id: Number(id), quantity: 1 }],
            sections,
            'hh-cart-shipbar',
            sectionId
          );
        })
        .catch(function (error) {
          // The cart was already mutated, so reload and show the shopper what is really in it.
          // Rolling the bar back and offering "Try Again" here charged for a second guard.
          if (posted || (error && error.hhCartTruthFailed)) {
            window.location.reload();
            return;
          }
          // Roll the optimistic paint back to the real pre-click state, otherwise the bar can
          // claim free shipping for an item that was never added.
          var bar = document.querySelector('[data-hh-shipbar]');
          if (bar && prevQty !== null) {
            bar.setAttribute('data-qty', prevQty);
            repaint(0);
          }
          btn.disabled = false;
          if (spin) spin.hidden = true;
          if (lbl) lbl.textContent = 'Try Again';
        });
    });
  }

  /* ---------------------------------------------------------------------
   * Mystery Hit Hero upsell
   * ------------------------------------------------------------------ */
  if (!window.__hhUpsellBound) {
    window.__hhUpsellBound = true;

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[data-hh-upsell-add]');
      if (!btn || btn.disabled) return;
      var el = btn.closest('[data-hh-upsell]');
      if (!el) return;
      var id = Number(el.getAttribute('data-variant'));
      if (!isFinite(id) || id < 1) return;
      var lbl = el.querySelector('[data-hh-upsell-label]');
      var spin = el.querySelector('[data-hh-upsell-spin]');
      // See the shipbar handler above: once the POST lands, a display failure is not an add
      // failure, and saying otherwise adds a second Mystery to the cart.
      var posted = false;
      btn.disabled = true;
      if (spin) spin.hidden = false;
      if (lbl) lbl.textContent = 'Adding';
      fetch(hhRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: id, quantity: 1 }],
          sections: 'cart-drawer-section',
          sections_url: window.location.pathname,
        }),
      })
        .then(function (r) {
          if (!r.ok) throw r;
          posted = true;
          return r.json();
        })
        .then(function (data) {
          return hhCartAfterAdd().then(function (cart) {
            return { data: data, cart: cart };
          });
        })
        .then(function (transaction) {
          var data = transaction.data;
          var cart = transaction.cart;
          var html = data.sections && data.sections['cart-drawer-section'];
          if (html) {
            var wrap = document.getElementById('shopify-section-cart-drawer-section');
            var fresh = new DOMParser()
              .parseFromString(html, 'text/html')
              .getElementById('shopify-section-cart-drawer-section');
            if (wrap && fresh) {
              wrap.replaceWith(fresh);
            }
            var d = document.getElementById('cart-drawer');
            if (d) {
              if (typeof d.open === 'function') {
                d.open();
              } else {
                d.setAttribute('open', '');
              }
            }
          } else {
            // Cart was mutated but no section came back; reload rather than show stale totals.
            window.location.reload();
            return;
          }
          hhDispatchCartLinesUpdate(
            cart,
            [{ id: id, quantity: 1 }],
            { 'cart-drawer-section': html },
            'hh-cart-upsell',
            'cart-drawer-section'
          );
        })
        .catch(function (error) {
          if (posted || (error && error.hhCartTruthFailed)) {
            window.location.reload();
            return;
          }
          btn.disabled = false;
          if (spin) spin.hidden = true;
          if (lbl) lbl.textContent = 'Try Again';
        });
    });
  }
})();
